"use client";

import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "motion/react";
import { Chip } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { 
  ArrowDown01Icon, 
  SparklesIcon, 
  CodeIcon, 
  AnalyticsUpIcon,
  CpuIcon
} from "@hugeicons/core-free-icons";

export default function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Track mouse coordinates for interactive parallax badges
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set(clientX / innerWidth - 0.5);
    mouseY.set(clientY / innerHeight - 0.5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const springX = useSpring(mouseX, { stiffness: 80, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 20 });

  // Translation values for the floating parallax badges
  const badge1X = useTransform(springX, [-0.5, 0.5], [-40, 40]);
  const badge1Y = useTransform(springY, [-0.5, 0.5], [-40, 40]);
  const badge2X = useTransform(springX, [-0.5, 0.5], [30, -30]);
  const badge2Y = useTransform(springY, [-0.5, 0.5], [30, -30]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        type: "spring" as const,
        stiffness: 150,
        damping: 18,
      },
    },
  };

  return (
    <section 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative min-h-[95vh] flex flex-col justify-center items-center overflow-hidden py-24 px-4 bg-neutral-50 dark:bg-black transition-colors duration-500"
    >
      {/* Background visual glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/10 dark:bg-blue-500/5 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 left-1/3 w-[400px] h-[400px] rounded-full bg-violet-500/10 dark:bg-violet-500/5 blur-[120px] pointer-events-none" />

      {/* Floating Badges (Parallax) */}
      <motion.div
        style={{ x: badge1X, y: badge1Y }}
        className="absolute left-[12%] top-[25%] hidden lg:flex items-center gap-3 p-3.5 rounded-2xl bg-white/70 dark:bg-neutral-900/40 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-800/40 shadow-xl pointer-events-none select-none"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/10 text-blue-600 dark:text-blue-400">
          <HugeiconsIcon icon={CodeIcon} className="w-5 h-5" size={20} />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-mono">Build Stack</p>
          <p className="text-xs font-bold text-slate-800 dark:text-white font-outfit">AI-Native Systems</p>
        </div>
      </motion.div>

      <motion.div
        style={{ x: badge2X, y: badge2Y }}
        className="absolute right-[12%] bottom-[30%] hidden lg:flex items-center gap-3 p-3.5 rounded-2xl bg-white/70 dark:bg-neutral-900/40 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-800/40 shadow-xl pointer-events-none select-none"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-purple-500/10 text-purple-600 dark:text-purple-400">
          <HugeiconsIcon icon={AnalyticsUpIcon} className="w-5 h-5" size={20} />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-mono">Metric</p>
          <p className="text-xs font-bold text-slate-800 dark:text-white font-outfit">99.2% Lighthouse Score</p>
        </div>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 flex flex-col gap-6 justify-center items-center text-center max-w-4xl"
      >
        {/* Modern Accent Chip */}
        <motion.div variants={itemVariants}>
          <Chip
            variant="soft"
            className="border border-blue-200/50 dark:border-blue-800/30 bg-blue-500/10 dark:bg-blue-500/5 text-blue-600 dark:text-blue-300 font-bold px-3 py-1 text-xs uppercase tracking-wider font-mono rounded-full flex items-center gap-1.5"
          >
            <HugeiconsIcon icon={SparklesIcon} className="w-3.5 h-3.5 text-yellow-500 animate-pulse" size={14} />
            Devebsy Agency
          </Chip>
        </motion.div>

        {/* Main Punchy Heading */}
        <motion.h1
          variants={itemVariants}
          className="text-4xl sm:text-6xl md:text-7xl font-bold text-slate-900 dark:text-white font-recoleta leading-[1.05] tracking-tight"
        >
          Crafting High-Performance
          <div className="mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-650 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent pb-1">
            Digital Products
          </div>
        </motion.h1>

        {/* Description Paragraph */}
        <motion.p
          variants={itemVariants}
          className="mx-auto max-w-2xl text-lg sm:text-xl text-neutral-600 dark:text-neutral-300 font-manjari font-light leading-relaxed px-4"
        >
          We partner with visionary startups and enterprise teams to engineer bespoke, 
          AI-native web platforms. We discard generic templates to deliver custom architectural 
          precision that turns user interactions into scalable business results.
        </motion.p>

        {/* Scroll Indicator */}
        <motion.div
          variants={itemVariants}
          className="mt-12 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] font-mono tracking-widest text-slate-400 dark:text-slate-650 uppercase">Scroll to discover</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="w-10 h-10 rounded-full flex items-center justify-center border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 shadow-md"
          >
            <HugeiconsIcon icon={ArrowDown01Icon} className="w-5 h-5" size={20} />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
